import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { Alert } from '../../components/ui/Alert';
import { Button, LinkButton } from '../../components/ui/Button';
import { Card, Chip } from '../../components/ui/Card';
import { Field } from '../../components/ui/Field';
import { usePayoutsReady } from '../payments/connect';
import { LoadingPanel } from '../../components/ui/Loader';
import { ApiError, api } from '../../lib/api';
import { useSession } from '../../stores/session';
import { Select, Textarea } from '../../components/ui/form';
import { useDocumentTitle } from '../../lib/title';
import {
  byName,
  countryName,
  guessTimezone,
  hostOptions,
  hosting,
  zoneLabel,
  type MyApplication,
} from './api';

interface Genre {
  id: number;
  name: string;
}

/** The design's ceiling, and the server's. */
const MAX_GENRES = 5;
const PITCH_MIN = 60;
const PITCH_MAX = 1500;

/**
 * Apply to host a room (HANDOFF §6.8).
 *
 * ## Four states, one page
 *
 * Not yet applied, waiting, turned down, approved. They are one route because
 * they are one question - "can I host?" - and a person who applied last week
 * and comes back to check should land somewhere that answers it rather than on
 * a form they have already filled in.
 *
 * Which state is shown comes from a single request, so the page cannot render a
 * form to somebody who is already approved.
 */
export function HostApplicationPage() {
  useDocumentTitle('Hosting');

  const user = useSession((state) => state.user);

  const { data, isPending } = useQuery({
    queryKey: ['host', 'application'],
    queryFn: () => hosting.mine(),
    enabled: Boolean(user),
  });

  /*
   * Up here with the other hooks, not down beside the approved branch that
   * uses it: this component returns early for signed-out and loading, and a
   * hook after those runs on some renders and not others.
   *
   * The hook itself is inert for anybody who is not an approved host with
   * payments on, so calling it unconditionally costs nothing.
   */
  const ready = usePayoutsReady();

  if (!user) {
    return (
      <Frame>
        <Card className="flex flex-col items-start gap-4 p-6">
          <p className="text-soft">Sign in to apply.</p>
          <LinkButton to="/login">Sign in</LinkButton>
        </Card>
      </Frame>
    );
  }

  if (isPending) {
    return (
      <Frame>
        <LoadingPanel />
      </Frame>
    );
  }

  if (data?.is_host) {
    return (
      <Frame hosting>
        <Card tone={ready === false ? 'default' : 'fresh'} className="flex flex-col items-start gap-3 p-6">
          <h2 className="text-lg font-semibold text-ink">
            {ready === false ? 'You are a host. One thing left.' : 'You are a host.'}
          </h2>
          <p className="text-soft">
            {ready === false
              ? /*
                 * Said here as well as on the console, because this is the page
                 * somebody lands on when they are told they were approved -
                 * and the approval mail does not know whether payouts are done.
                 *
                 * The console link stays: a room can be named, filled and set
                 * up while Stripe is still checking documents. Only going on
                 * air waits.
                 */
                'Set up payouts below before you go on air — people can tip and boost the moment you are live, and that money needs somewhere to land. You can build your room in the meantime.'
              : 'The console is where you set up a room, queue tracks and go live.'}
          </p>
          <LinkButton to="/console">Open the console</LinkButton>
        </Card>

        {/*
          Getting paid has its own area now, because Stripe needs a page per
          surface - payouts and balances at their own addresses - and this is
          the form somebody fills in once. The prompt stays here, since this is
          where a newly approved host lands.
        */}
        <Card className="flex flex-col items-start gap-3 p-6">
          <h2 className="text-sm font-medium uppercase tracking-wider text-faint">Earnings</h2>
          <p className="text-sm text-soft">
            {ready === false
              ? 'Set up payouts before you go on air — people can tip and boost the moment you are live, and that money needs somewhere to land.'
              : 'What you have been paid, your payout history and your balance.'}
          </p>
          <LinkButton to="/earnings">{ready === false ? 'Set up payouts' : 'Open earnings'}</LinkButton>
        </Card>

        {data.application && (
          <Card className="p-6">
            <h2 className="mb-1 text-sm font-medium uppercase tracking-wider text-faint">
              Your application
            </h2>
            <p className="mb-4 text-sm text-soft">
              What you told us when you applied. This is the record a moderator
              approved, so it is kept as it was — your room's name, description
              and submission rules are in{' '}
              <Link to="/console" className="text-accent hover:underline">
                the console
              </Link>
              .
            </p>
            <Summary application={data.application} />
          </Card>
        )}
      </Frame>
    );
  }

  const application = data?.application ?? null;

  if (application?.status === 'pending') {
    return (
      <Frame>
        <PendingCard application={application} />
      </Frame>
    );
  }

  return (
    <Frame>
      {application?.status === 'declined' && <DeclinedNotice application={application} />}
      <ApplicationForm previous={application} />
    </Frame>
  );
}

/**
 * The page's own heading, which depends on who is reading it.
 *
 * "Host a room… there is a short application first" is exactly right for
 * somebody deciding whether to apply, and nonsense to somebody who was approved
 * a month ago and came here to check their payouts. One route, two audiences,
 * so the header says which one it is talking to.
 */
function Frame({ children, hosting = false }: { children: React.ReactNode; hosting?: boolean }) {
  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-6 px-6 py-10">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-ink">
          {hosting ? 'Hosting' : 'Host a room'}
        </h1>
        <p className="mt-1.5 max-w-prose text-sm text-soft">
          {hosting
            ? 'Your room, how you get paid, and what you told us when you applied.'
            : 'Rooms are live sets anyone can drop into. Hosting one means playing records to an audience in real time, so there is a short application first.'}
        </p>
      </div>
      {children}
    </div>
  );
}

function PendingCard({ application }: { application: MyApplication }) {
  const queryClient = useQueryClient();

  const withdraw = useMutation({
    mutationFn: () => hosting.withdraw(application.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['host', 'application'] }),
  });

  return (
    // The hook goes on a wrapper, not on `Card`: Card renders only the props it
    // declares, so anything else is silently dropped - which is a real bug the
    // first time somebody writes a test against an attribute that never
    // reached the DOM.
    <div data-application-pending>
      <Card className="flex flex-col gap-5 p-6">
        <div className="flex flex-col gap-1">
          <h2 className="text-lg font-semibold text-ink">We have your application.</h2>
          <p className="text-sm text-soft">
            Somebody reads every one of these. Applications are answered oldest first, and you will
            get a notification either way.
          </p>
        </div>

        <Summary application={application} />

        {withdraw.isError && (
          <Alert tone="error">
            {withdraw.error instanceof ApiError
              ? withdraw.error.message
              : 'That could not be withdrawn.'}
          </Alert>
        )}

        <div className="flex flex-col items-start gap-2 border-t border-line pt-5">
          {/* Withdrawing is mostly used by people who reread their own pitch, so
            the copy says what it is actually for. */}
          <p className="text-xs text-faint">
            Want to rewrite it? Withdraw this one and apply again — there is no penalty.
          </p>
          <Button
            variant="ghost"
            loading={withdraw.isPending}
            onClick={() => withdraw.mutate()}
            data-withdraw
          >
            Withdraw application
          </Button>
        </div>
      </Card>
    </div>
  );
}

function DeclinedNotice({ application }: { application: MyApplication }) {
  return (
    <Alert tone="info">
      <div className="flex flex-col gap-2">
        <strong className="font-medium">Your last application was not accepted.</strong>
        {application.decision_note ? (
          <p className="text-sm">{application.decision_note}</p>
        ) : (
          <p className="text-sm">No reason was given.</p>
        )}
        {/* The honest framing: a decline here is "not yet", and the form below
            is pre-filled with what they wrote so rewriting is editing. */}
        <p className="text-sm">You can apply again below.</p>
      </div>
    </Alert>
  );
}

function Summary({ application }: { application: MyApplication }) {
  return (
    <dl className="grid gap-4 text-sm sm:grid-cols-2">
      <div>
        <dt className="meta-caps text-faint">Country</dt>
        <dd className="mt-1 text-ink">{countryName(application.country)}</dd>
      </div>
      <div>
        <dt className="meta-caps text-faint">Timezone</dt>
        <dd className="mt-1 font-mono text-xs text-ink">{zoneLabel(application.timezone)}</dd>
      </div>
      <div className="sm:col-span-2">
        <dt className="meta-caps text-faint">Genres</dt>
        <dd className="mt-1.5 flex flex-wrap gap-1.5">
          {application.genres.map((genre) => (
            <Chip key={genre.id}>{genre.name}</Chip>
          ))}
        </dd>
      </div>
      <div className="sm:col-span-2">
        <dt className="meta-caps text-faint">Your show</dt>
        <dd className="mt-1 whitespace-pre-wrap text-soft">{application.pitch}</dd>
      </div>
    </dl>
  );
}

/**
 * The form.
 *
 * ## What it does not ask
 *
 * No legal name, no address, no date of birth, no documents. A host does not
 * need an identity check to play records to forty people, and collecting
 * documents this platform has no process for verifying would be theatre with a
 * data breach attached.
 *
 * Everything it does ask, a decision or a schedule depends on. The field-level
 * comments say which.
 */
function ApplicationForm({ previous }: { previous: MyApplication | null }) {
  const queryClient = useQueryClient();

  const { data: options } = useQuery({
    queryKey: ['host', 'options'],
    queryFn: () => hostOptions(),
    // The tz database. It changes a few times a year.
    staleTime: 24 * 60 * 60 * 1000,
  });

  const { data: genres } = useQuery({
    queryKey: ['genres'],
    queryFn: () => api.get<{ items: Genre[] }>('/genres'),
    staleTime: 60 * 60 * 1000,
  });

  /*
   * Pre-filled from the previous application when there is one.
   *
   * Somebody reapplying after a decline is editing, not starting over, and
   * making them retype a paragraph they already wrote is the fastest way to get
   * a worse second application than the first.
   */
  const [country, setCountry] = useState(previous?.country ?? '');
  const [timezone, setTimezone] = useState(previous?.timezone ?? guessTimezone());
  const [pitch, setPitch] = useState(previous?.pitch ?? '');
  const [experience, setExperience] = useState(previous?.experience ?? '');
  const [picked, setPicked] = useState<number[]>(previous?.genres.map((g) => g.id) ?? []);
  const [agreed, setAgreed] = useState(false);

  const countryList = useMemo(() => byName(options?.countries ?? []), [options]);

  const apply = useMutation({
    mutationFn: () =>
      hosting.apply({
        country,
        timezone,
        pitch: pitch.trim(),
        experience: experience.trim() || null,
        genres: picked,
        rights_ack: agreed,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['host', 'application'] }),
  });

  const toggleGenre = (id: number) => {
    setPicked((current) => {
      if (current.includes(id)) return current.filter((g) => g !== id);
      if (current.length >= MAX_GENRES) return current;

      return [...current, id];
    });
  };

  const ready =
    country !== '' &&
    timezone !== '' &&
    pitch.trim().length >= PITCH_MIN &&
    picked.length > 0 &&
    agreed;

  const submit = (event: FormEvent) => {
    event.preventDefault();

    if (ready) apply.mutate();
  };

  return (
    <Card className="p-6">
      <form onSubmit={submit} className="flex flex-col gap-6" data-host-form>
        <div className="grid gap-5 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="host-country" className="text-sm font-medium text-soft">
              Where are you?
            </label>
            <Select
              id="host-country"
              value={country}
              disabled={apply.isPending}
              data-host-country
              placeholder="Pick a country"
              options={countryList.map((entry) => ({
                value: entry.code,
                label: entry.name,
              }))}
              onChange={setCountry}
            />
            {/* Said plainly, because "why do you need my country" is a fair
                question and the answer is not "marketing". */}
            <p className="text-xs text-faint">
              Broadcasting rules differ by country. This is the only reason we ask.
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="host-timezone" className="text-sm font-medium text-soft">
              Your timezone
            </label>
            <Select
              id="host-timezone"
              value={timezone}
              disabled={apply.isPending}
              data-host-timezone
              options={(options?.timezones ?? [timezone]).map((zone) => ({
                value: zone,
                label: zoneLabel(zone),
              }))}
              onChange={setTimezone}
            />
            <p className="text-xs text-faint">So shows get scheduled when you are awake.</p>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-sm font-medium text-soft">
              What would you play? Up to {MAX_GENRES}.
            </span>
            <span className="font-mono text-xs text-faint">
              {picked.length}/{MAX_GENRES}
            </span>
          </div>

          <div className="flex flex-wrap gap-1.5">
            {(genres?.items ?? []).map((genre) => {
              const on = picked.includes(genre.id);
              const full = picked.length >= MAX_GENRES && !on;

              return (
                <button
                  key={genre.id}
                  type="button"
                  aria-pressed={on}
                  disabled={full || apply.isPending}
                  data-genre={genre.id}
                  onClick={() => toggleGenre(genre.id)}
                  className={`rounded-[var(--radius-pill)] border px-3.5 py-2 text-sm transition-colors ${
                    on
                      ? 'border-transparent bg-accent text-accent-ink'
                      : 'border-line text-soft hover:border-line-strong hover:text-ink'
                  } ${full ? 'cursor-not-allowed opacity-40' : ''}`}
                >
                  {genre.name}
                </button>
              );
            })}
          </div>

          {/* Order is preference order, and it is not obvious that clicking
              order is what sets it - so it says so, and shows the result. */}
          {picked.length > 1 && (
            <p className="text-xs text-faint">
              In the order you picked them:{' '}
              {picked
                .map((id) => genres?.items.find((g) => g.id === id)?.name)
                .filter(Boolean)
                .join(' → ')}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="host-pitch" className="text-sm font-medium text-soft">
            What is your show?
          </label>
          <Textarea
            id="host-pitch"
            className="min-h-36"
            value={pitch}
            maxLength={PITCH_MAX}
            disabled={apply.isPending}
            data-host-pitch
            placeholder="What it sounds like, how long it runs, how often, and who it is for."
            onChange={(event) => setPitch(event.target.value)}
          />
          <div className="flex justify-between">
            <p className="text-xs text-faint">
              This is the part somebody reads. It is the whole application.
            </p>
            <span
              className={`font-mono text-xs ${
                pitch.trim().length < PITCH_MIN ? 'text-faint' : 'text-fresh'
              }`}
            >
              {pitch.trim().length}/{PITCH_MIN} min
            </span>
          </div>
        </div>

        <Field
          label="Done this before?"
          name="experience"
          value={experience}
          maxLength={500}
          disabled={apply.isPending}
          placeholder="A mix, a station, a night you run — or leave it blank."
          onChange={(event) => setExperience(event.target.value)}
          hint="Optional, and it genuinely is. Nobody has hosted anything before their first time."
        />

        {/*
          The undertaking. A checkbox rather than buried small print, because it
          is the one thing on this form that creates an obligation - and it is
          stored with the application, so what was agreed and when is a record
          rather than a claim about a form.
        */}
        <label className="flex cursor-pointer items-start gap-3 rounded-[var(--radius-card)] border border-line bg-app p-4">
          <input
            type="checkbox"
            checked={agreed}
            disabled={apply.isPending}
            data-host-rights
            onChange={(event) => setAgreed(event.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--color-accent)]"
          />
          <span className="text-sm text-soft">
            I will only play music I have the right to broadcast, and I understand a room can be
            taken down if I do not.
          </span>
        </label>

        {apply.isError && (
          <Alert tone="error">
            {apply.error instanceof ApiError ? apply.error.message : 'That could not be sent.'}
          </Alert>
        )}

        <div className="flex items-center gap-3">
          <Button type="submit" loading={apply.isPending} disabled={!ready}>
            Send application
          </Button>
          <Link to="/" className="text-sm text-soft hover:text-ink">
            Not now
          </Link>
        </div>
      </form>
    </Card>
  );
}
